import { Box, Typography, TextField, Grid, Link as MuiLink } from '@mui/material';
import type { ReactNode } from 'react';
import CpaxLogo from '../CpaxLogo';

// Shared form container component
export function FormContainer({ 
  children, 
  maxWidth = 608 
}: { 
  children: ReactNode; 
  maxWidth?: number; 
}) {
  return (
    <Box sx={{ width: '100%', maxWidth }}>
      {children}
    </Box>
  );
}

// Shared form header component
export function FormHeader({
  title,
  subtitle,
  icon
}: {
  title: string;
  subtitle: string;
  icon?: ReactNode;
}) {
  return (
    <>
      {icon && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
          {icon}
        </Box>
      )}
      
      <Typography
        variant="h4"
        sx={{
          fontSize: { xs: 24, sm: 32 },
          fontWeight: 400,
          lineHeight: 1.235,
          letterSpacing: '0.25px',
          color: 'primary.main',
          textAlign: 'center',
          mb: 2,
        }}
      >
        {title}
      </Typography>
      
      <Typography
        variant="body1"
        sx={{
          color: 'text.secondary',
          textAlign: 'center',
          mb: 4,
        }}
      >
        {subtitle}
      </Typography>
    </>
  );
}

// Shared error display component
export function ErrorDisplay({ 
  error, 
  onRetry 
}: { 
  error: string | null; 
  onRetry?: () => void; 
}) {
  if (!error) return null;

  return (
    <Box sx={{ textAlign: 'center', mb: 2 }}>
      <Typography variant="body2" color="error">
        {error}
      </Typography>
      {onRetry && (
        <MuiLink
          component="button"
          onClick={onRetry}
          sx={{
            mt: 1,
            px: 1,
            py: 0.5,
            color: 'primary.main',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Retry
        </MuiLink>
      )}
    </Box>
  );
}

// Shared name fields component
export function NameFields({
  firstNameValue = '',
  lastNameValue = '',
  firstNameLabel = 'First Name',
  lastNameLabel = 'Last Name',
  required = true,
  firstNameError,
  lastNameError
}: {
  firstNameValue?: string;
  lastNameValue?: string;
  firstNameLabel?: string;
  lastNameLabel?: string;
  required?: boolean;
  firstNameError?: string;
  lastNameError?: string;
}) {
  return (
    <>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          name="firstName"
          label={firstNameLabel}
          defaultValue={firstNameValue}
          required={required}
          fullWidth
          variant="outlined"
          error={Boolean(firstNameError)}
          helperText={firstNameError}
        />
      </Grid>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
          name="lastName"
          label={lastNameLabel}
          defaultValue={lastNameValue}
          required={required}
          fullWidth
          variant="outlined"
          error={Boolean(lastNameError)}
          helperText={lastNameError}
        />
      </Grid>
    </>
  );
}

// Shared left panel component
export function LeftBrandContainer({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        flex: { xs: '0 0 auto', md: '0 0 44%' },
        width: { xs: '100%' },
        height: { xs: 280, md: 'auto' },
        minHeight: { md: '100vh' },
        borderRadius: { xs: '16px', md: '20px' },
        background: 'linear-gradient(160deg, #0c200f 0%, #1a4328 55%, #0d3d1c 100%)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        px: { xs: 4, md: 7 },
        py: { xs: 5, md: 8 },
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      {/* Subtle grid texture */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          opacity: 0.06,
          backgroundImage: `
            linear-gradient(rgba(86,151,55,0.5) 1px, transparent 1px),
            linear-gradient(90deg, rgba(86,151,55,0.5) 1px, transparent 1px)
          `,
          backgroundSize: '60px 60px',
          pointerEvents: 'none',
        }}
      />

      {/* Content */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: { xs: 'center', md: 'flex-start' },
          maxWidth: 420,
          textAlign: { xs: 'center', md: 'left' },
          color: 'white',
        }}
      >
        <Box sx={{ mb: { xs: 3, md: 6 }, opacity: 0.9 }}>
          <CpaxLogo height={40} variant="light" />
        </Box>

        {children}
      </Box>
    </Box>
  );
}

// Shared left panel content
export function LeftBrandContent({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string;
}) {
  return (
    <>
      <Typography
        variant="h3"
        sx={{
          fontSize: { xs: 24, md: 30 },
          fontWeight: 700,
          lineHeight: 1.25,
          letterSpacing: '-0.02em',
          color: 'rgba(255,255,255,0.95)',
          mb: 2.5,
        }}
      >
        {title}
      </Typography>

      {subtitle && (
        <Typography
          variant="body1"
          sx={{
            fontSize: { xs: 15, md: 16 },
            fontWeight: 400,
            lineHeight: 1.6,
            color: 'rgba(255,255,255,0.55)',
          }}
        >
          {subtitle}
        </Typography>
      )}
    </>
  );
}

// Shared page layout component
export function AuthPageLayout({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#f8f9fb',
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        position: 'relative',
        p: { xs: 1.5, md: 1.5 },
        gap: 0,
      }}
    >
      {children}
    </Box>
  );
}

// Shared right content area
export function RightContentArea({ children }: { children: ReactNode }) {
  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: { xs: 4, md: 0 },
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 608,
          px: { xs: 3, sm: 4 },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}